import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "integrations/firebase/firestore";
import { IBook, IBookInputs, bookDocumentSchema } from "models/book";
import { EGenerationStatus } from "models/generationStatus";
import { IReferenceDocument } from "models/reference";
import { parseQueryDocs } from "services/documentParsing";
import { commitInBatches } from "utils/firestoreBatch";

const MAX_TRANSACTION_WRITES = 500;

const LAST_BOOK_LOOKBACK = 5;

type TBookCreationData = {
  book_inputs: IBookInputs;
  userId: string;
};

type TBookUpdateData = Partial<TBookCreationData> & {
  narrative_extraction_status?: EGenerationStatus;
};

const assertTransactionCapacity = (operationCount: number): void => {
  if (operationCount > MAX_TRANSACTION_WRITES) {
    throw new Error(
      `This change requires ${operationCount} writes, exceeding Firestore's ${MAX_TRANSACTION_WRITES}-write transaction limit.`,
    );
  }
};

const getNumber = (snapshot: {
  data: () => Record<string, unknown> | undefined;
}): number => {
  const number = snapshot.data()?.number;
  if (typeof number !== "number") throw new Error("Document number is invalid");
  return number;
};

const getPartId = (snapshot: {
  data: () => Record<string, unknown> | undefined;
}): string => {
  const partId = snapshot.data()?.part_id;
  if (typeof partId !== "string") throw new Error("Chapter part is invalid");
  return partId;
};

const getPartNumber = (snapshot: {
  data: () => Record<string, unknown> | undefined;
}): number => {
  const partNumber = snapshot.data()?.part_number;
  if (typeof partNumber !== "number") {
    throw new Error("Chapter part number is invalid");
  }
  return partNumber;
};

const getStructureVersion = (snapshot: {
  data: () => Record<string, unknown> | undefined;
}): number => {
  const structureVersion = snapshot.data()?.chapter_structure_version;
  if (structureVersion === undefined) return 0;
  if (
    typeof structureVersion !== "number" ||
    !Number.isInteger(structureVersion) ||
    structureVersion < 0
  ) {
    throw new Error("Book chapter structure version is invalid");
  }
  return structureVersion;
};

const assertStructureVersion = (
  snapshot: {
    exists: () => boolean;
    data: () => Record<string, unknown> | undefined;
  },
  expectedVersion: number,
): void => {
  if (!snapshot.exists()) throw new Error("Book not found");
  if (getStructureVersion(snapshot) !== expectedVersion) {
    throw new Error("Book chapter structure changed; reload and try again");
  }
};

const nextStructureVersion = (expectedVersion: number) => ({
  chapter_structure_version: expectedVersion + 1,
  updatedAt: serverTimestamp(),
});

export const saveBook = async (data: TBookCreationData): Promise<string> => {
  const booksRef = collection(db, "books");
  const bookDoc = {
    ...data,
    book_deletion_status: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(booksRef, bookDoc);
  return docRef.id;
};

export const updateBook = async (
  id: string,
  data: TBookUpdateData,
): Promise<void> => {
  const bookRef = doc(db, "books", id);
  await updateDoc(bookRef, { ...data, updatedAt: serverTimestamp() });
};

export const createBookReference = async (
  bookId: string,
  reference: IReferenceDocument,
): Promise<void> => {
  await setDoc(
    doc(db, "books", bookId, "referenceDocuments", reference.id),
    reference,
  );
};

export const removeBookReference = async (
  bookId: string,
  referenceId: string,
): Promise<void> => {
  const chunksRef = collection(
    db,
    "books",
    bookId,
    "referenceDocuments",
    referenceId,
    "chunks",
  );
  const chunkDocs = await getDocs(chunksRef);
  await commitInBatches(
    chunkDocs.docs.map((chunkDoc) => (batch) => batch.delete(chunkDoc.ref)),
  );

  await deleteDoc(doc(db, "books", bookId, "referenceDocuments", referenceId));
};

export const updateAuthorStylePrompt = async (
  bookId: string,
  authorStylePrompt: string,
): Promise<void> => {
  await updateDoc(doc(db, "books", bookId), {
    author_style_prompt: authorStylePrompt,
    updatedAt: serverTimestamp(),
  });
};

export const getLastBookForUser = async (
  userId: string,
): Promise<IBook | null> => {
  if (!userId) return null;

  const userBooksQuery = query(
    collection(db, "books"),
    where("userId", "==", userId),
    where("book_deletion_status", "==", null),
    orderBy("createdAt", "desc"),
    limit(LAST_BOOK_LOOKBACK),
  );
  const querySnapshot = await getDocs(userBooksQuery);
  if (querySnapshot.empty) return null;

  const { data, errors } = parseQueryDocs(bookDocumentSchema, querySnapshot);
  if (data.length > 0) return data[0];
  throw errors[0];
};

export const updatePart = async (
  bookId: string,
  partId: string,
  data: { title?: string },
): Promise<void> => {
  await updateDoc(doc(db, "books", bookId, "bookParts", partId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const updateChapterInPart = async (
  bookId: string,
  chapterId: string,
  data: { title?: string; thesis?: string },
): Promise<void> => {
  await updateDoc(doc(db, "books", bookId, "chapters", chapterId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const addChapterToPart = async (
  bookId: string,
  partId: string,
  afterChapterId?: string,
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const partRef = doc(bookRef, "bookParts", partId);
  const chaptersRef = collection(bookRef, "chapters");
  const chapterRef = doc(chaptersRef);
  const [bookBeforeTransaction, chaptersSnapshot] = await Promise.all([
    getDoc(bookRef),
    getDocs(query(chaptersRef, where("part_id", "==", partId))),
  ]);
  const expectedVersion = getStructureVersion(bookBeforeTransaction);

  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, partSnapshot] = await Promise.all([
      transaction.get(bookRef),
      transaction.get(partRef),
    ]);
    const chapterSnapshots = await Promise.all(
      chaptersSnapshot.docs.map((chapter) => transaction.get(chapter.ref)),
    );
    assertStructureVersion(bookSnapshot, expectedVersion);
    if (!partSnapshot.exists()) throw new Error("Part not found");

    const chapters = chapterSnapshots.toSorted(
      (first, second) => getNumber(first) - getNumber(second),
    );
    const afterIndex = afterChapterId
      ? chapters.findIndex((chapter) => chapter.id === afterChapterId)
      : chapters.length - 1;
    if (afterChapterId && afterIndex === -1)
      throw new Error("Chapter not found");

    assertTransactionCapacity(chapters.length - afterIndex + 1);
    chapters.slice(afterIndex + 1).forEach((chapter) => {
      transaction.update(chapter.ref, { number: getNumber(chapter) + 1 });
    });
    transaction.set(chapterRef, {
      id: chapterRef.id,
      part_id: partId,
      part_number: getNumber(partSnapshot),
      number: afterIndex + 2,
      title: "New Chapter",
      thesis: "",
      reference_ids: {},
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};

export const deleteChapterFromPart = async (
  bookId: string,
  chapterId: string,
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const chapterRef = doc(bookRef, "chapters", chapterId);
  const chaptersRef = collection(bookRef, "chapters");
  const [bookBeforeTransaction, chapterBeforeTransaction] = await Promise.all([
    getDoc(bookRef),
    getDoc(chapterRef),
  ]);
  if (!chapterBeforeTransaction.exists()) throw new Error("Chapter not found");
  const expectedVersion = getStructureVersion(bookBeforeTransaction);
  const partId = getPartId(chapterBeforeTransaction);
  const chaptersSnapshot = await getDocs(
    query(chaptersRef, where("part_id", "==", partId)),
  );

  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, chapterSnapshot] = await Promise.all([
      transaction.get(bookRef),
      transaction.get(chapterRef),
    ]);
    assertStructureVersion(bookSnapshot, expectedVersion);
    if (!chapterSnapshot.exists()) throw new Error("Chapter not found");
    const chapterSnapshots = await Promise.all(
      chaptersSnapshot.docs.map((chapter) => transaction.get(chapter.ref)),
    );
    const chapters = chapterSnapshots.toSorted(
      (first, second) => getNumber(first) - getNumber(second),
    );
    const deletedIndex = chapters.findIndex(
      (chapter) => chapter.id === chapterId,
    );
    if (deletedIndex === -1) throw new Error("Chapter not found");

    assertTransactionCapacity(chapters.length + 1);
    transaction.delete(chapterRef);
    chapters.slice(deletedIndex + 1).forEach((chapter) => {
      transaction.update(chapter.ref, { number: getNumber(chapter) - 1 });
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};

export const addPart = async (
  bookId: string,
  afterPartNumber?: number,
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const partsRef = collection(bookRef, "bookParts");
  const chaptersRef = collection(bookRef, "chapters");
  const newPartRef = doc(partsRef);
  const [bookBeforeTransaction, partsSnapshot, chaptersSnapshot] =
    await Promise.all([
      getDoc(bookRef),
      getDocs(partsRef),
      getDocs(chaptersRef),
    ]);
  const expectedVersion = getStructureVersion(bookBeforeTransaction);

  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, partSnapshots, chapterSnapshots] = await Promise.all([
      transaction.get(bookRef),
      Promise.all(partsSnapshot.docs.map((part) => transaction.get(part.ref))),
      Promise.all(
        chaptersSnapshot.docs.map((chapter) => transaction.get(chapter.ref)),
      ),
    ]);
    assertStructureVersion(bookSnapshot, expectedVersion);
    const parts = partSnapshots.toSorted(
      (first, second) => getNumber(first) - getNumber(second),
    );
    const insertionIndex =
      afterPartNumber === undefined
        ? parts.length
        : parts.findIndex((part) => getNumber(part) === afterPartNumber) + 1;
    if (insertionIndex === 0) throw new Error("Part not found");

    const shiftedParts = parts.slice(insertionIndex);
    const shiftedPartIds = new Set(shiftedParts.map((part) => part.id));
    const shiftedChapters = chapterSnapshots.filter((chapter) =>
      shiftedPartIds.has(getPartId(chapter)),
    );

    assertTransactionCapacity(shiftedParts.length + shiftedChapters.length + 2);
    shiftedParts.forEach((part) => {
      transaction.update(part.ref, { number: getNumber(part) + 1 });
    });
    shiftedChapters.forEach((chapter) => {
      transaction.update(chapter.ref, {
        part_number: getPartNumber(chapter) + 1,
      });
    });
    transaction.set(newPartRef, {
      id: newPartRef.id,
      number: insertionIndex + 1,
      title: "New Part",
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};

export const deletePart = async (
  bookId: string,
  partId: string,
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const partRef = doc(bookRef, "bookParts", partId);
  const partsRef = collection(bookRef, "bookParts");
  const chaptersRef = collection(bookRef, "chapters");
  const [
    bookBeforeTransaction,
    partBeforeTransaction,
    partsSnapshot,
    chaptersSnapshot,
  ] = await Promise.all([
    getDoc(bookRef),
    getDoc(partRef),
    getDocs(partsRef),
    getDocs(chaptersRef),
  ]);
  if (!partBeforeTransaction.exists()) throw new Error("Part not found");
  const expectedVersion = getStructureVersion(bookBeforeTransaction);

  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, partSnapshot, partSnapshots, chapterSnapshots] =
      await Promise.all([
        transaction.get(bookRef),
        transaction.get(partRef),
        Promise.all(
          partsSnapshot.docs.map((part) => transaction.get(part.ref)),
        ),
        Promise.all(
          chaptersSnapshot.docs.map((chapter) => transaction.get(chapter.ref)),
        ),
      ]);
    assertStructureVersion(bookSnapshot, expectedVersion);
    if (!partSnapshot.exists()) throw new Error("Part not found");

    const deletedPartNumber = getNumber(partSnapshot);
    const chapters = chapterSnapshots;
    const deletedChapters = chapters.filter(
      (chapter) => getPartId(chapter) === partId,
    );
    const shiftedParts = partSnapshots.filter(
      (part) => getNumber(part) > deletedPartNumber,
    );
    const shiftedPartIds = new Set(shiftedParts.map((part) => part.id));
    const shiftedChapters = chapters.filter((chapter) =>
      shiftedPartIds.has(getPartId(chapter)),
    );

    assertTransactionCapacity(
      2 + deletedChapters.length + shiftedParts.length + shiftedChapters.length,
    );
    transaction.delete(partRef);
    deletedChapters.forEach((chapter) => transaction.delete(chapter.ref));
    shiftedParts.forEach((part) => {
      transaction.update(part.ref, { number: getNumber(part) - 1 });
    });
    shiftedChapters.forEach((chapter) => {
      transaction.update(chapter.ref, {
        part_number: getPartNumber(chapter) - 1,
      });
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};

export const reorderChaptersInPart = async (
  bookId: string,
  partId: string,
  reorderedChapterIds: string[],
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const chaptersRef = collection(db, "books", bookId, "chapters");
  const [bookBeforeTransaction, chaptersSnapshot] = await Promise.all([
    getDoc(bookRef),
    getDocs(query(chaptersRef, where("part_id", "==", partId))),
  ]);
  const expectedVersion = getStructureVersion(bookBeforeTransaction);
  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, chapterSnapshots] = await Promise.all([
      transaction.get(bookRef),
      Promise.all(
        chaptersSnapshot.docs.map((chapter) => transaction.get(chapter.ref)),
      ),
    ]);
    assertStructureVersion(bookSnapshot, expectedVersion);
    const chaptersById = new Map(
      chapterSnapshots.map((chapter) => [chapter.id, chapter]),
    );
    if (
      reorderedChapterIds.length !== chaptersById.size ||
      new Set(reorderedChapterIds).size !== chaptersById.size ||
      reorderedChapterIds.some((chapterId) => !chaptersById.has(chapterId))
    ) {
      throw new Error("Chapter order does not match this part");
    }

    assertTransactionCapacity(reorderedChapterIds.length + 1);
    reorderedChapterIds.forEach((chapterId, index) => {
      const chapter = chaptersById.get(chapterId);
      if (chapter && getNumber(chapter) !== index + 1) {
        transaction.update(chapter.ref, { number: index + 1 });
      }
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};

export const moveChapter = async (
  bookId: string,
  chapterId: string,
  targetPartId: string,
  targetIndex: number,
): Promise<void> => {
  const bookRef = doc(db, "books", bookId);
  const chapterRef = doc(bookRef, "chapters", chapterId);
  const targetPartRef = doc(bookRef, "bookParts", targetPartId);
  const chaptersRef = collection(bookRef, "chapters");
  const [
    bookBeforeTransaction,
    chapterBeforeTransaction,
    targetPartBeforeTransaction,
  ] = await Promise.all([
    getDoc(bookRef),
    getDoc(chapterRef),
    getDoc(targetPartRef),
  ]);
  if (!chapterBeforeTransaction.exists()) throw new Error("Chapter not found");
  if (!targetPartBeforeTransaction.exists()) {
    throw new Error("Target part not found");
  }
  const expectedVersion = getStructureVersion(bookBeforeTransaction);
  const sourcePartId = getPartId(chapterBeforeTransaction);
  const sourceChaptersSnapshot = await getDocs(
    query(chaptersRef, where("part_id", "==", sourcePartId)),
  );
  const targetChaptersSnapshot =
    sourcePartId === targetPartId
      ? sourceChaptersSnapshot
      : await getDocs(query(chaptersRef, where("part_id", "==", targetPartId)));

  await runTransaction(db, async (transaction) => {
    const [bookSnapshot, chapterSnapshot, targetPartSnapshot] =
      await Promise.all([
        transaction.get(bookRef),
        transaction.get(chapterRef),
        transaction.get(targetPartRef),
      ]);
    assertStructureVersion(bookSnapshot, expectedVersion);
    if (!chapterSnapshot.exists()) throw new Error("Chapter not found");
    if (!targetPartSnapshot.exists()) throw new Error("Target part not found");

    const [sourceChapterSnapshots, targetChapterSnapshots] = await Promise.all([
      Promise.all(
        sourceChaptersSnapshot.docs.map((chapter) =>
          transaction.get(chapter.ref),
        ),
      ),
      Promise.all(
        targetChaptersSnapshot.docs.map((chapter) =>
          transaction.get(chapter.ref),
        ),
      ),
    ]);
    const sourceChapters = sourceChapterSnapshots.toSorted(
      (first, second) => getNumber(first) - getNumber(second),
    );
    const targetChapters = (
      sourcePartId === targetPartId ? sourceChapters : targetChapterSnapshots
    ).toSorted((first, second) => getNumber(first) - getNumber(second));
    const sourceIndex = sourceChapters.findIndex(
      (chapter) => chapter.id === chapterId,
    );
    if (sourceIndex === -1) throw new Error("Chapter not found");

    if (sourcePartId === targetPartId) {
      if (targetIndex < 1 || targetIndex > sourceChapters.length) {
        throw new Error("Target chapter position is invalid");
      }
      const reordered = [...sourceChapters];
      const [movedChapter] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex - 1, 0, movedChapter);
      assertTransactionCapacity(reordered.length + 1);
      reordered.forEach((chapter, index) => {
        if (getNumber(chapter) !== index + 1) {
          transaction.update(chapter.ref, { number: index + 1 });
        }
      });
      transaction.update(bookRef, nextStructureVersion(expectedVersion));
      return;
    }

    if (targetIndex < 1 || targetIndex > targetChapters.length + 1) {
      throw new Error("Target chapter position is invalid");
    }
    assertTransactionCapacity(
      sourceChapters.length + targetChapters.length + 1,
    );
    sourceChapters.slice(sourceIndex + 1).forEach((chapter) => {
      transaction.update(chapter.ref, { number: getNumber(chapter) - 1 });
    });
    targetChapters
      .filter((chapter) => getNumber(chapter) >= targetIndex)
      .forEach((chapter) => {
        transaction.update(chapter.ref, { number: getNumber(chapter) + 1 });
      });
    transaction.update(chapterRef, {
      part_id: targetPartId,
      part_number: getNumber(targetPartSnapshot),
      number: targetIndex,
    });
    transaction.update(bookRef, nextStructureVersion(expectedVersion));
  });
};
